import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  FPS_LIMITS,
  GRAPHICS_PRESETS,
  LANGUAGES,
  type GraphicsPreset,
  type Language,
  type Settings,
} from '@il-colpo/shared/settings';
import { useAccount } from '../../account/accountStore.ts';
import { useAppStore } from '../../state/appStore.ts';
import { usePwaStore } from '../../pwa/pwaStore.ts';
import pwaStyles from '../../pwa/Pwa.module.css';
import { useSettingsStore } from '../../state/settingsStore.ts';
import { useToastStore } from '../../state/toastStore.ts';
import { useMediaQuery } from '../../systems/motion.ts';
import { audio as audioManager } from '../../audio/manager.ts';
import {
  Button,
  Icon,
  Panel,
  ScreenFrame,
  SegmentedControl,
  Slider,
  Switch,
  Tabs,
} from '../../ui/index.ts';
import type { IconName } from '../../ui/index.ts';
import { ControlsTab } from './ControlsTab.tsx';
import styles from './Settings.module.css';

type TabKey =
  | 'audio'
  | 'graphics'
  | 'game'
  | 'accessibility'
  | 'controls'
  | 'language'
  | 'notifications'
  | 'account';

const TAB_ICONS: Record<TabKey, IconName> = {
  audio: 'volume',
  graphics: 'palette',
  game: 'controller',
  accessibility: 'eye',
  controls: 'keyboard',
  language: 'globe',
  notifications: 'bell',
  account: 'shield',
};

const TAB_ORDER: readonly TabKey[] = [
  'audio',
  'graphics',
  'game',
  'accessibility',
  'controls',
  'language',
  'notifications',
  'account',
];

const percent = (v: number) => `${Math.round(v * 100)}%`;

export function SettingsScreen() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const [tab, setTab] = useState<TabKey>('audio');
  const wide = useMediaQuery('(min-width: 860px)');
  const showGallery = import.meta.env.DEV || import.meta.env.VITE_ENABLE_GALLERY === 'true';

  return (
    <ScreenFrame title={t('title')} backTo="/">
      <Tabs
        tabs={TAB_ORDER.map((key) => ({ key, label: t(`tabs.${key}`), icon: TAB_ICONS[key] }))}
        active={tab}
        onChange={setTab}
        orientation={wide ? 'vertical' : 'horizontal'}
        label={t('title')}
      >
        <Panel as="div" className={styles['panel']}>
          {tab === 'audio' ? <AudioTab /> : null}
          {tab === 'graphics' ? <GraphicsTab /> : null}
          {tab === 'game' ? <GameTab /> : null}
          {tab === 'accessibility' ? <AccessibilityTab /> : null}
          {tab === 'controls' ? <ControlsTab /> : null}
          {tab === 'language' ? <LanguageTab /> : null}
          {tab === 'notifications' ? <NotificationsTab /> : null}
          {tab === 'account' ? <AccountTab /> : null}
        </Panel>
      </Tabs>
      <InstallRow />
      <footer className={styles['footer']}>
        <span>{tc('version', { version: __APP_VERSION__ })}</span>
        {showGallery ? (
          <Link to="/design-system" className={styles['galleryLink']}>
            {t('footer.gallery')}
          </Link>
        ) : null}
      </footer>
    </ScreenFrame>
  );
}

/** The browser's offer to install the app, when it makes one. */
function InstallRow() {
  const { t } = useTranslation('pwa');
  const canInstall = usePwaStore((s) => s.canInstall);
  const install = usePwaStore((s) => s.install);
  const push = useToastStore((s) => s.push);
  if (!canInstall) return null;
  return (
    <div className={pwaStyles['install']}>
      <Button
        variant="secondary"
        onClick={() => {
          void install().then((accepted) => {
            if (accepted) push(t('installed'), 'success');
          });
        }}
      >
        {t('install')}
      </Button>
      <p className={pwaStyles['note']}>{t('installNote')}</p>
    </div>
  );
}

function useSettingsUpdate() {
  return useSettingsStore((s) => s.update);
}

function AudioTab() {
  const { t } = useTranslation('settings');
  const audio = useSettingsStore((s) => s.settings.audio);
  const update = useSettingsUpdate();
  const set = (key: 'master' | 'music' | 'sfx') => (value: number) => {
    update((s) => {
      s.audio[key] = value;
    });
  };
  return (
    <div className={styles['stack']}>
      <Slider
        label={t('audio.master')}
        value={audio.master}
        valueText={percent(audio.master)}
        onChange={set('master')}
      />
      <Slider
        label={t('audio.music')}
        value={audio.music}
        valueText={percent(audio.music)}
        onChange={set('music')}
      />
      <Slider
        label={t('audio.sfx')}
        value={audio.sfx}
        valueText={percent(audio.sfx)}
        onChange={set('sfx')}
      />
      <Button
        variant="secondary"
        icon="volume"
        onClick={() => {
          // A flourish of the real thing: the player hears the three sliders at work.
          void audioManager.unlock().then(() => {
            audioManager.sfx('stinger.success');
            audioManager.sfx('coins');
          });
        }}
      >
        {t('audio.test')}
      </Button>
      <p className={styles['note']}>{t('audio.note')}</p>
    </div>
  );
}

function GraphicsTab() {
  const { t } = useTranslation('settings');
  const g = useSettingsStore((s) => s.settings.graphics);
  const detected = useAppStore((s) => s.detectedPreset);
  const update = useSettingsUpdate();
  const toggle =
    (
      key:
        | 'batterySaver'
        | 'bloom'
        | 'grain'
        | 'chromaticAberration'
        | 'volumetricLight'
        | 'rain'
        | 'particles',
    ) =>
    (value: boolean) => {
      update((s) => {
        s.graphics[key] = value;
      });
    };
  const presetOptions: { value: 'auto' | GraphicsPreset; label: string }[] = [
    { value: 'auto', label: t('graphics.presets.auto') },
    ...GRAPHICS_PRESETS.map((p) => ({ value: p, label: t(`graphics.presets.${p}`) })),
  ];
  return (
    <div className={styles['stack']}>
      <SegmentedControl
        label={t('graphics.preset')}
        value={g.preset}
        options={presetOptions}
        onChange={(preset) => {
          update((s) => {
            s.graphics.preset = preset;
          });
        }}
      />
      {g.preset === 'auto' ? (
        <p className={styles['note']}>
          {t('graphics.detected', { preset: t(`graphics.presets.${detected}`) })}
        </p>
      ) : null}
      <SegmentedControl
        label={t('graphics.fpsLimit')}
        value={g.fpsLimit}
        options={FPS_LIMITS.map((fps) => ({ value: fps, label: String(fps) }))}
        onChange={(fpsLimit) => {
          update((s) => {
            s.graphics.fpsLimit = fpsLimit;
          });
        }}
      />
      <Switch
        label={t('graphics.batterySaver')}
        hint={t('graphics.batterySaverHint')}
        checked={g.batterySaver}
        onChange={toggle('batterySaver')}
      />
      <h3 className={styles['subhead']}>{t('graphics.effects')}</h3>
      <Switch label={t('graphics.bloom')} checked={g.bloom} onChange={toggle('bloom')} />
      <Switch label={t('graphics.grain')} checked={g.grain} onChange={toggle('grain')} />
      <Switch
        label={t('graphics.chromaticAberration')}
        checked={g.chromaticAberration}
        onChange={toggle('chromaticAberration')}
      />
      <Switch
        label={t('graphics.volumetricLight')}
        checked={g.volumetricLight}
        onChange={toggle('volumetricLight')}
      />
      <Switch label={t('graphics.rain')} checked={g.rain} onChange={toggle('rain')} />
      <Switch
        label={t('graphics.particles')}
        checked={g.particles}
        onChange={toggle('particles')}
      />
    </div>
  );
}

function GameTab() {
  const { t } = useTranslation('settings');
  const game = useSettingsStore((s) => s.settings.game);
  const update = useSettingsUpdate();
  return (
    <div className={styles['stack']}>
      <Switch
        label={t('game.confirmations')}
        hint={t('game.confirmationsHint')}
        checked={game.confirmations}
        onChange={(v) => {
          update((s) => {
            s.game.confirmations = v;
          });
        }}
      />
      <Switch
        label={t('game.hints')}
        checked={game.hints}
        onChange={(v) => {
          update((s) => {
            s.game.hints = v;
          });
        }}
      />
      <Slider
        label={t('game.animationSpeed')}
        min={0.5}
        max={2}
        step={0.25}
        value={game.animationSpeed}
        valueText={t('game.animationSpeedValue', { value: game.animationSpeed })}
        onChange={(v) => {
          update((s) => {
            s.game.animationSpeed = v;
          });
        }}
      />
    </div>
  );
}

type MotionChoice = 'system' | 'on' | 'off';

function toChoice(v: Settings['accessibility']['reduceMotion']): MotionChoice {
  return v === null ? 'system' : v ? 'on' : 'off';
}

function AccessibilityTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const a = useSettingsStore((s) => s.settings.accessibility);
  const uiScale = useSettingsStore((s) => s.settings.uiScale);
  const update = useSettingsUpdate();
  const toggle =
    (key: 'colorblind' | 'largeText' | 'highContrast' | 'vibration' | 'leftHanded') =>
    (value: boolean) => {
      update((s) => {
        s.accessibility[key] = value;
      });
    };
  return (
    <div className={styles['stack']}>
      <Switch
        label={t('accessibility.colorblind')}
        hint={t('accessibility.colorblindHint')}
        checked={a.colorblind}
        onChange={toggle('colorblind')}
      />
      <Switch
        label={t('accessibility.largeText')}
        checked={a.largeText}
        onChange={toggle('largeText')}
      />
      <Switch
        label={t('accessibility.highContrast')}
        checked={a.highContrast}
        onChange={toggle('highContrast')}
      />
      <SegmentedControl<MotionChoice>
        label={t('accessibility.reduceMotion')}
        value={toChoice(a.reduceMotion)}
        options={[
          { value: 'system', label: t('accessibility.reduceMotionSystem') },
          { value: 'on', label: tc('on') },
          { value: 'off', label: tc('off') },
        ]}
        onChange={(choice) => {
          update((s) => {
            s.accessibility.reduceMotion = choice === 'system' ? null : choice === 'on';
          });
        }}
      />
      <p className={styles['note']}>{t('accessibility.reduceMotionHint')}</p>
      <Switch
        label={t('accessibility.vibration')}
        checked={a.vibration}
        onChange={toggle('vibration')}
      />
      <Switch
        label={t('accessibility.leftHanded')}
        checked={a.leftHanded}
        onChange={toggle('leftHanded')}
      />
      <Slider
        label={t('accessibility.uiScale')}
        min={0.8}
        max={1.5}
        step={0.05}
        value={uiScale}
        valueText={percent(uiScale)}
        onChange={(v) => {
          update((s) => {
            s.uiScale = v;
          });
        }}
      />
    </div>
  );
}

function LanguageTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const language = useSettingsStore((s) => s.settings.language);
  const update = useSettingsUpdate();
  return (
    <div className={styles['stack']}>
      <SegmentedControl<Language>
        label={t('language.title')}
        value={language}
        options={LANGUAGES.map((l) => ({ value: l, label: tc(`languages.${l}`) }))}
        onChange={(l) => {
          update((s) => {
            s.language = l;
          });
        }}
      />
    </div>
  );
}

function NotificationsTab() {
  const { t } = useTranslation('settings');
  const n = useSettingsStore((s) => s.settings.notifications);
  const update = useSettingsUpdate();
  const toggle = (key: 'invites' | 'friends' | 'events' | 'season') => (value: boolean) => {
    update((s) => {
      s.notifications[key] = value;
    });
  };
  return (
    <div className={styles['stack']}>
      <Switch label={t('notifications.invites')} checked={n.invites} onChange={toggle('invites')} />
      <Switch label={t('notifications.friends')} checked={n.friends} onChange={toggle('friends')} />
      <Switch label={t('notifications.events')} checked={n.events} onChange={toggle('events')} />
      <Switch label={t('notifications.season')} checked={n.season} onChange={toggle('season')} />
      <p className={styles['note']}>{t('notifications.note')}</p>
    </div>
  );
}

function AccountTab() {
  const { t } = useTranslation('settings');
  const { t: tl } = useTranslation('legal');
  const navigate = useNavigate();
  const available = useAccount((s) => s.setup.available);
  return (
    <div className={styles['stack']}>
      <div className={styles['accountNote']}>
        <Icon name="shield" size="2.25rem" />
        <p>{available ? t('account.openHint') : t('account.note')}</p>
      </div>
      {available ? (
        <div>
          <Button
            onClick={() => {
              void navigate('/account');
            }}
          >
            {t('account.open')}
          </Button>
        </div>
      ) : null}
      <p className={styles['note']}>
        <Link className={styles['galleryLink']} to="/legal/terms">
          {tl('links.terms')}
        </Link>
        {' · '}
        <Link className={styles['galleryLink']} to="/legal/privacy">
          {tl('links.privacy')}
        </Link>
      </p>
    </div>
  );
}
